import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';

import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../../MyComponents/LandingSections/SectionEyebrow';
import GroupCard from '../../../MyComponents/GroupCard/GroupCard';
import { MANAGE_CLASSES_LINK } from '../../../common/urlLinks';
import { CARD, TEXT_LINK } from './dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

const useStyles = makeStyles(() => ({
    head: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px 24px',
        marginBottom: '22px',
    },
    eyebrow: {
        marginBottom: '14px',
    },
    title: {
        margin: 0,
        fontSize: 'clamp(1.5rem, 2.6vw, 2.1rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    all: {
        ...TEXT_LINK,
        paddingBottom: '4px',
    },
    // auto-fill rather than auto-fit: a single group should take one column,
    // not stretch across the whole row.
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
        gap: '18px',
    },
    empty: {
        ...CARD,
        padding: 'clamp(24px, 3vw, 36px)',
        background: REDESIGN_COLORS.mist,
        borderColor: REDESIGN_COLORS.primaryBorderSoft,
    },
    emptyTitle: {
        margin: '0 0 8px',
        fontSize: '1.2rem',
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    emptyText: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '0.95rem',
        lineHeight: 1.55,
    },
}));

/**
 * The most recently used groups, mine and the ones I was invited to, each with
 * its one action. The full list lives on the groups page, linked from the
 * heading.
 */
const RecentGroups = ({ groups, total, profile, onStart, loading }) => {
    const classes = useStyles();

    if (loading) return null;

    return (
        <LandingSection background="none" gutter={0} paddingTop={0} paddingBottom={48}>
            <div className={classes.head}>
                <div>
                    <div className={classes.eyebrow}>
                        <SectionEyebrow>
                            <FormattedMessage id="redesign.home.dashboard.recent.eyebrow" />
                        </SectionEyebrow>
                    </div>
                    <h2 className={classes.title}>
                        <FormattedMessage id="redesign.home.dashboard.recent.title" />
                    </h2>
                </div>
                {total > 0 ? (
                    <Link to={MANAGE_CLASSES_LINK} className={classes.all}>
                        <FormattedMessage id="redesign.page.home.loggedIn.seeMoreGroupsButton" />
                        <span aria-hidden="true">→</span>
                    </Link>
                ) : null}
            </div>

            {groups.length ? (
                <div className={classes.grid}>
                    {groups.map((row) => (
                        <GroupCard
                            key={`${row.isMine ? 'mine' : 'guest'}-${row.isMine ? row.id : row.classId}`}
                            row={row}
                            profile={profile}
                            onStart={onStart}
                        />
                    ))}
                </div>
            ) : (
                <div className={classes.empty}>
                    <h3 className={classes.emptyTitle}>
                        <FormattedMessage id="redesign.home.dashboard.recent.emptyTitle" />
                    </h3>
                    <p className={classes.emptyText}>
                        <FormattedMessage id="redesign.home.dashboard.recent.emptyText" />
                    </p>
                </div>
            )}
        </LandingSection>
    );
};

export default RecentGroups;
